'use client';

import { Suspense, useEffect, useRef, useState } from 'react';

import { usePathname, useSearchParams } from 'next/navigation';

import LoadingBar, { type LoadingBarRef } from 'react-top-loading-bar';

import {
  getRouteKey,
  shouldStartRouteProgress,
} from '~/lib/routing/route-progress';

/**
 * Navigations that resolve faster than this never paint a bar, so an instant
 * transition doesn't flash.
 */
const START_DELAY_MS = 150;

/**
 * A click we read as a navigation may not produce one (a handler cancels it,
 * the response redirects off-site). Retire the bar rather than leave it
 * crawling forever.
 */
const SAFETY_TIMEOUT_MS = 15_000;

/**
 * Some navigations call `router.push` directly, with no anchor for the click
 * listener below to read — the command palette (⌘K) picks a result this way.
 * They dispatch this window event instead so the bar still covers the
 * click-to-first-byte gap. An event keeps any programmatic caller decoupled
 * from the single root-mounted bar; `startRouteProgress` is the only publisher.
 */
export const ROUTE_PROGRESS_START_EVENT = 'arkero:route-progress-start';

/**
 * Start the route progress bar for a navigation with no anchor. Call this
 * immediately before a programmatic `router.push`. No-op on the server.
 */
export function startRouteProgress() {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new Event(ROUTE_PROGRESS_START_EVENT));
}

/**
 * Top-of-viewport progress bar for every in-app route transition.
 *
 * Route-level `loading.tsx` skeletons only cover segments that declare one, and
 * even there they say nothing about the gap between the click and the first
 * byte. This is mounted once at the root so the bar is the one piece of
 * feedback that is always present; skeletons render underneath it.
 */
export function RouteProgressBar() {
  // `useSearchParams` suspends during static rendering; the boundary keeps that
  // contained instead of opting the tree that mounts us into client rendering.
  return (
    <Suspense fallback={null}>
      <RouteProgressIndicator />
    </Suspense>
  );
}

function RouteProgressIndicator() {
  const barRef = useRef<LoadingBarRef>(null);
  const started = useRef(false);
  const committedRoute = useRef('');
  const [pending, setPending] = useState(false);

  const pathname = usePathname();
  const search = useSearchParams().toString();

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target;

      const anchor =
        target instanceof Element
          ? target.closest<HTMLAnchorElement>('a[href]')
          : null;

      if (shouldStartRouteProgress(event, anchor, window.location.href)) {
        setPending(true);
      }
    };

    // By the time `popstate` fires, `location` is already the entry being
    // restored. A back/forward that only moves the hash leaves the route key
    // untouched, so nothing would ever complete the bar.
    const onPopState = () => {
      const restored = getRouteKey(
        window.location.pathname,
        window.location.search,
      );

      if (restored !== committedRoute.current) {
        setPending(true);
      }
    };

    // Programmatic navigations (the command palette) have no anchor to read, so
    // they announce themselves through this event.
    const onManualStart = () => setPending(true);

    // Capture phase: `next/link` calls `preventDefault()` in its own handler,
    // so by the bubble phase every router navigation looks cancelled.
    document.addEventListener('click', onClick, true);
    window.addEventListener('popstate', onPopState);
    window.addEventListener(ROUTE_PROGRESS_START_EVENT, onManualStart);

    return () => {
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener(ROUTE_PROGRESS_START_EVENT, onManualStart);
    };
  }, []);

  // The App Router commits the URL only once the transition lands, so the
  // committed path/query changing is the completion signal.
  useEffect(() => {
    committedRoute.current = getRouteKey(pathname, search);
    setPending(false);
  }, [pathname, search]);

  useEffect(() => {
    const bar = barRef.current;

    if (!bar) {
      return;
    }

    if (!pending) {
      // `complete()` jumps the bar to 100% and fades it out, so calling it on a
      // bar that never started paints a full-width flash — on first mount, and
      // on every navigation that beat the delay below.
      if (started.current) {
        started.current = false;
        bar.complete();
      }

      return;
    }

    const startTimer = setTimeout(() => {
      started.current = true;
      bar.continuousStart(20, 400);
    }, START_DELAY_MS);

    const safetyTimer = setTimeout(() => setPending(false), SAFETY_TIMEOUT_MS);

    return () => {
      clearTimeout(startTimer);
      clearTimeout(safetyTimer);
    };
  }, [pending]);

  // `LoadingBar` takes no arbitrary props, so neither of its two divs can carry
  // a `data-test`. The wrapper tags the widget, and `className` — which the
  // library puts on the *fill* element, the one whose width tracks progress —
  // carries a hook class so tests can target it without walking the library's
  // internal div nesting. Styling stays in `bg-primary`; `route-progress-fill`
  // is a selector only.
  return (
    <div data-test="route-progress-bar">
      <LoadingBar
        className="route-progress-fill bg-primary"
        color=""
        height={3}
        shadow
        waitingTime={0}
        ref={barRef}
      />
    </div>
  );
}
